/**
 * Mi Equipo — Gestión de usuarios de la empresa del usuario logueado.
 * Visible para roles admin y superadmin.
 * Diferente de "Usuarios Plataforma" (Admin) que gestiona todos los usuarios globales.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { StyledDialog } from './common';
import { cardStyle, iconBoxStyle } from './common/naraCards';
import { EmptyUsers, NaraLoader } from './common/NaraIllustrations';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

const ESTADO_CONFIG = {
  aprobado: { color: '#10b981', label: 'Aprobado', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  pendiente: { color: '#f59e0b', label: 'Pendiente', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> },
  rechazado: { color: '#ef4444', label: 'Rechazado', icon: <CancelIcon sx={{ fontSize: 14 }} /> },
};

const ROL_CONFIG = {
  admin: { color: '#8b5cf6', label: 'Admin', icon: <AdminPanelSettingsIcon sx={{ fontSize: 14 }} /> },
  superadmin: { color: '#ef4444', label: 'Superadmin', icon: <AdminPanelSettingsIcon sx={{ fontSize: 14 }} /> },
  miembro: { color: '#0891b2', label: 'Miembro', icon: <PersonIcon sx={{ fontSize: 14 }} /> },
};

function getEmpresaId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.company_id || user.empresa_id || '1';
  } catch { return '1'; }
}

function getEmpresaNombre() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.company_name || user.empresa_nombre || '';
  } catch { return ''; }
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function MiEquipo() {
  const empresaId = getEmpresaId();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogRechazo, setDialogRechazo] = useState(false);
  const [rechazoUser, setRechazoUser] = useState(null);

  const cargarUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/usuarios?empresa_id=${empresaId}`, { headers: getAuthHeaders() });
      setUsuarios(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargarUsuarios(); }, [cargarUsuarios]);

  const handleAprobar = async (userId) => {
    try {
      const res = await fetch(`${API_BASE}/admin/usuarios/${userId}/aprobar`, {
        method: 'PATCH', headers: getAuthHeaders()
      });
      if (res.ok) cargarUsuarios();
      else { const err = await res.json(); alert(err.detail || 'Error'); }
    } catch (e) { console.error(e); }
  };

  const handleRechazar = async () => {
    if (!rechazoUser) return;
    try {
      const res = await fetch(`${API_BASE}/admin/usuarios/${rechazoUser.id}/rechazar`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({})
      });
      if (res.ok) { setDialogRechazo(false); cargarUsuarios(); }
      else { const err = await res.json(); alert(err.detail || 'Error'); }
    } catch (e) { console.error(e); }
  };

  const pendientes = usuarios.filter(u => u.estado_aprobacion === 'pendiente');
  const activos = usuarios.filter(u => u.estado_aprobacion !== 'pendiente');

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={iconBoxStyle('linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', 'rgba(8, 145, 178, 0.4)')}>
          <GroupsIcon />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#134e4a', fontWeight: 700, fontSize: 22 }}>Mi Equipo</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>Usuarios de tu empresa — aprueba solicitudes y gestiona accesos</Typography>
        </Box>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><NaraLoader size={56} /></Box>}

      {/* Solicitudes pendientes */}
      {!loading && pendientes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: 15, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HourglassEmptyIcon sx={{ fontSize: 18 }} /> Solicitudes pendientes ({pendientes.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pendientes.map(u => (
              <Box key={u.id} sx={{ ...cardStyle, p: 0, borderLeftColor: '#f59e0b' }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#f59e0b15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <PersonIcon sx={{ color: '#f59e0b' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#134e4a' }}>{u.name} {u.lastname}</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 12 }}>{u.email}</Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: 11 }}>
                      Solicitud: {u.created_at ? new Date(u.created_at).toLocaleDateString('es-CO') : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" onClick={() => handleAprobar(u.id)}
                      sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2, boxShadow: '0 2px 8px rgba(16,185,129,0.3)', '&:hover': { bgcolor: '#059669' } }}>
                      Aprobar
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => { setRechazoUser(u); setDialogRechazo(true); }}
                      sx={{ borderColor: '#ef4444', color: '#ef4444', textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2, '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' } }}>
                      Rechazar
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Miembros del equipo */}
      {!loading && (
        <Box>
          <Typography sx={{ color: '#134e4a', fontWeight: 700, fontSize: 15, mb: 1.5 }}>
            Miembros del equipo ({activos.length})
          </Typography>
          {activos.length === 0 && pendientes.length === 0 && (
            <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
              <EmptyUsers size={80} />
              <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>Sin miembros</Typography>
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>Los usuarios que se registren con el NIT de tu empresa aparecerán aquí</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activos.map(u => {
              const estado = ESTADO_CONFIG[u.estado_aprobacion] || ESTADO_CONFIG.aprobado;
              const rol = ROL_CONFIG[u.role] || ROL_CONFIG.miembro;
              return (
                <Box key={u.id} sx={{ ...cardStyle, p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: rol.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <PersonIcon sx={{ color: rol.color, fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#134e4a' }}>{u.name} {u.lastname}</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 12 }}>{u.email}</Typography>
                  </Box>
                  <Chip label={rol.label} size="small" icon={rol.icon}
                    sx={{ bgcolor: rol.color + '15', color: rol.color, fontWeight: 600, fontSize: 11, height: 22 }} />
                  <Chip label={estado.label} size="small" icon={estado.icon}
                    sx={{ bgcolor: estado.color + '15', color: estado.color, fontWeight: 600, fontSize: 11, height: 22 }} />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <StyledDialog open={dialogRechazo} onClose={() => setDialogRechazo(false)}
        title="Rechazar solicitud" icon={<CancelIcon />} variant="compact" maxWidth="xs">
        <StyledDialog.Body compact>
          {rechazoUser && (
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              ¿Rechazar la solicitud de <b>{rechazoUser.name} {rechazoUser.lastname}</b> ({rechazoUser.email})?
            </Typography>
          )}
        </StyledDialog.Body>
        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setDialogRechazo(false)} />
          <StyledDialog.PrimaryButton fullWidth onClick={handleRechazar}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
            Rechazar
          </StyledDialog.PrimaryButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}
